import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../../../../hooks/usePlusWebsites';
import type { SectionDetails } from './useGetSectionDetails';
import { sectionDetailsQueryKey } from './useGetSectionDetails';

interface UpdateSectionParams {
  websiteId: string;
  pageId: string;
  sectionId: string;
  data: Record<string, unknown>;
}

function updateSection({ websiteId, pageId, sectionId, data }: UpdateSectionParams) {
  return axiosPrivate
    .patch(`/v3/pharmacist/plus-websites/${websiteId}/pages/${pageId}/sections/${sectionId}`, {
      data,
    })
    .then((r) => r.data);
}

export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSection,
    // Write the saved data straight into the section-details cache. The edit form seeds from
    // this cache on open (RJSF's uncontrolled `initialFormData`), so without this a reopened
    // editor would show the pre-save data until a full page refresh cleared the cache.
    onSuccess: (_data, { websiteId, pageId, sectionId, data }) => {
      queryClient.setQueryData<SectionDetails>(
        sectionDetailsQueryKey(websiteId, pageId, sectionId),
        (previous) => (previous ? { ...previous, data } : previous)
      );
      markWebsiteAsChanged(queryClient, websiteId);
    },
    meta: {
      successMessage: 'Section updated successfully',
      errorMessage: 'Failed to update section',
    },
  });
}
